import { fetchExpenseClaimReceipt } from '@query'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type Props = {
  slug: string
  claimId: number
  expenseId: number
  mimeType: string
}

export const ExpenseClaimReceiptPreview: React.FC<Props> = ({
  slug,
  claimId,
  expenseId,
  mimeType
}) => {
  const [t] = useTranslation()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: [slug, 'expense-claim-receipt', claimId, expenseId],
    queryFn: () => fetchExpenseClaimReceipt(slug, claimId, expenseId),
    staleTime: 60_000
  })

  useEffect(() => {
    if (!data) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(data)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [data])

  if (isLoading) {
    return <PreviewFrame $isLoading />
  }
  if (isError || !previewUrl) {
    return <PreviewFrame />
  }
  if (mimeType.startsWith('image/')) {
    return <PreviewImage src={previewUrl} alt="" />
  }
  if (mimeType === 'application/pdf') {
    return <PdfFrame src={previewUrl} title={t('travelAndExpenses.expense.preview')} />
  }
  return <PreviewFrame />
}

const PreviewFrame = styled.div<{ $isLoading?: boolean }>`
  width: 100%;
  height: ${({ theme }) => theme.size.receiptPreviewHeight}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme, $isLoading }) =>
    $isLoading ? theme.colors.defaultHover : theme.colors.neutralGray};
`

const PreviewImage = styled.img`
  width: 100%;
  max-height: ${({ theme }) => theme.size.receiptPreviewHeight}rem;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme }) => theme.colors.neutralGray};
`

const PdfFrame = styled.iframe`
  width: 100%;
  height: ${({ theme }) => theme.size.receiptPreviewHeight}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`
